import { Redirect, Route, RouteProps } from 'react-router-dom'
import qs from 'qs'
import { withSentryRouting } from '@sentry/react'

import { Loading } from '../../shared/loading'
import PageAccessError from '../../shared/errors/PageAccessError'
import { Roles } from '../../../types'
import { userHasPermission, userHasRole } from '../../../lib/authenticationUtils'
import { AdminUserPermission } from '../../../generated/graphql'
import { useAuth } from '../../provider/OIDCProvider'

// Create Custom Sentry Route component for track parameterized transaction names (i.e. the paths)
const SentryRoute = withSentryRouting(Route)

/** Note: Only use roles OR permission, there shouldn't be a reason to use both. */
type ProtectedRouteProps = {
  /** Optional permission to apply to route.
   * If a list of permissions is provided, access is granted if the user
   * has at least one permission.  Access to all permissions is not required.
   */
  permissions?: AdminUserPermission | Array<AdminUserPermission>
  /** Optional roles to apply to a route.
   * If no roles are provide, it's assumed all role have access. */
  roles?: Array<Roles>
}

const ProtectedRoute = ({
  children,
  permissions,
  roles,
  ...rest
}: ProtectedRouteProps & RouteProps) => {
  const { isInitializing, isAuthenticated, user } = useAuth()

  if (isInitializing || (isAuthenticated && !user)) {
    return <Loading />
  }

  const canAccess = () => {
    if (user) {
      if (permissions) {
        return userHasPermission(user, permissions)
      }
      if (!roles || userHasRole(user, roles)) {
        return true
      }
    }
    return false
  }

  if (isAuthenticated && user && !canAccess()) {
    return <PageAccessError />
  }

  const getDefaultLocation = (location: any) => {
    let pathname = '/login'
    if (location.search.includes('errorType')) {
      const search = qs.parse(location.search, { ignoreQueryPrefix: true })
      const { errorType } = search

      pathname = `/login?errorType=${errorType}`
    }
    return (
      <Redirect
        to={{
          pathname,
          state: { from: location }
        }}
      />
    )
  }

  return (
    <SentryRoute
      {...rest}
      render={({ location }) => (isAuthenticated && user ? children : getDefaultLocation(location))}
    />
  )
}

export default ProtectedRoute
